import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { ScanLine, X, Flashlight, FlashlightOff } from "lucide-react";

export default function BarcodeScanner({ open, onDetected, onClose, label = "serial number" }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const trackRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  const [error, setError] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);

  onDetectedRef.current = onDetected;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setTorchOn(false);
    setTorchAvailable(false);
    const reader = new BrowserMultiFormatReader();

    (async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } } },
          videoRef.current,
          (result, err, ctrl) => {
            if (cancelled) return;
            if (!controlsRef.current) controlsRef.current = ctrl;
            if (result) {
              const text = result.getText().trim();
              stop();
              onDetectedRef.current(text);
            }
          }
        );
        controlsRef.current = controls;
        // detect torch capability on the active camera track
        try {
          const stream = videoRef.current && videoRef.current.srcObject;
          const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
          trackRef.current = track || null;
          const caps = track && track.getCapabilities && track.getCapabilities();
          if (!cancelled && caps && "torch" in caps) setTorchAvailable(true);
        } catch (e) {}
      } catch (e) {
        if (!cancelled) {
          setError(
            "Could not access the camera. Check camera permissions, or type the value manually."
          );
        }
      }
    })();

    function stop() {
      try {
        controlsRef.current && controlsRef.current.stop();
      } catch (e) {}
      controlsRef.current = null;
      trackRef.current = null;
    }

    return () => {
      cancelled = true;
      stop();
    };
  }, [open]);

  const toggleTorch = async () => {
    const track = trackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch (e) {}
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" /> Scan {label}
          </DialogTitle>
          <DialogDescription>
            Point the camera at the barcode. It will fill in automatically.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p className="rounded-lg bg-red-50 px-3 py-3 text-sm font-medium text-red-600" data-testid="scanner-error">
            {error}
          </p>
        ) : (
          <div className="relative overflow-hidden rounded-xl bg-black">
            <video ref={videoRef} className="h-64 w-full object-cover" muted playsInline />
            <div className="pointer-events-none absolute inset-x-6 top-1/2 h-0.5 -translate-y-1/2 bg-primary/80 shadow-[0_0_12px_2px_rgba(27,107,76,0.6)]" />
            <div className="pointer-events-none absolute inset-4 rounded-lg border-2 border-white/60" />
            {torchAvailable && (
              <button
                type="button"
                onClick={toggleTorch}
                data-testid="torch-toggle"
                aria-label={torchOn ? "Turn off flashlight" : "Turn on flashlight"}
                className={`absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full backdrop-blur transition-colors ${
                  torchOn ? "bg-amber-400 text-slate-900" : "bg-black/50 text-white hover:bg-black/70"
                }`}
              >
                {torchOn ? <Flashlight className="h-5 w-5" /> : <FlashlightOff className="h-5 w-5" />}
              </button>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border bg-white text-sm font-semibold text-slate-600 hover:bg-slate-50"
          data-testid="scanner-cancel"
        >
          <X className="h-4 w-4" /> Cancel
        </button>
      </DialogContent>
    </Dialog>
  );
}
